import * as React from "react";
import type { ButtonColor } from "../../Button/Button.types";
import { ConfirmModal } from "..";

interface ConfirmOptions {
  confirmColor?: ButtonColor;
}

interface ConfirmState {
  title: string;
  body: string;
  confirmLabel: string;
  confirmColor: ButtonColor;
  resolve: (confirmed: boolean) => void;
}

/**
 * Returns a `confirm(title, body, confirmLabel?, confirmColor?)` async function
 * and a `<ConfirmModal />` element to render in your component tree.
 *
 * Usage:
 *   const { confirm, ConfirmModal } = useConfirmModal();
 *   // in JSX: <ConfirmModal />
 *   // in handler: if (!(await confirm("Delete?", "This cannot be undone.", "Delete"))) return;
 */
export function useConfirmModal() {
  const [state, setState] = React.useState<ConfirmState | null>(null);

  const confirm = React.useCallback(
    (
      title: string,
      body: string,
      confirmLabel = "Confirm",
      confirmColor: ButtonColor = "danger",
    ): Promise<boolean> =>
      new Promise((resolve) => {
        setState({ title, body, confirmLabel, confirmColor, resolve });
      }),
    [],
  );

  const handleConfirm = React.useCallback(() => {
    state?.resolve(true);
    setState(null);
  }, [state]);

  const handleCancel = React.useCallback(() => {
    state?.resolve(false);
    setState(null);
  }, [state]);

  const ConfirmModalElement = React.useCallback(
    () => (
      <ConfirmModal
        open={state !== null}
        title={state?.title ?? ""}
        body={state?.body ?? ""}
        confirmLabel={state?.confirmLabel ?? "Confirm"}
        confirmColor={state?.confirmColor ?? "danger"}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    ),
    [state, handleCancel, handleConfirm],
  );

  return { confirm, ConfirmModal: ConfirmModalElement };
}
